// Operator surface scaffold.
// Layered gating: Cloudflare Access OTP (configured on the CF dashboard,
// scoped to /operator*) sits in front; the server-side gate is hybrid
// (verified CF Access JWT OR X-Operator-Token). On mount we probe the
// operator API once with no token: if it returns 200, the JWT cookie
// is already satisfying the server-side gate and we skip the legacy
// token form entirely. If it returns 403, we fall back to the form
// (localhost dev + non-CF callers). A stored token short-circuits the
// probe.
//
// The token, when present, lives in sessionStorage (cleared on tab
// close) rather than localStorage — an op signs in once per tab
// session, a future XSS or rogue browser extension can't read a
// long-lived token, and password managers don't try to capture it.
import { useEffect, useState, type FormEvent } from "react";
import { OperatorDashboard } from "./OperatorDashboard";
import { DEFAULT_SERVER, isSameOriginHost } from "../lib/server-config";

const OPERATOR_TOKEN_STORAGE_KEY = "aurora.cloud.operatorToken";

// "" is the JWT-auth sentinel — token has no value, but auth passed
// upstream. The discriminator lives in `mode` so render code can
// distinguish probe / form / ready without leaning on token nullness.
type AuthState =
  | { mode: "probing" }
  | { mode: "form" }
  | { mode: "ready"; token: string };

function readStoredToken(): string | null {
  try {
    const raw = sessionStorage.getItem(OPERATOR_TOKEN_STORAGE_KEY);
    if (raw && typeof raw === "string" && raw.length > 0) return raw;
  } catch {
    // sessionStorage unavailable
  }
  return null;
}

// Resolve the base the operator surface calls. The operator API authenticates
// via the CF Access JWT, which the browser only presents to the same origin
// Access gates (pilot.plaintosee.cc/operator*). In prod we therefore route
// through the same-origin Pages Function bridge at /operator/api/*, which
// forwards the injected JWT to the VPS — a direct cross-origin call to
// app.plaintosee.cc would bypass Access, carry no JWT, and force the legacy
// token form. In Vite dev (localhost / 127.0.0.1) we call the origin directly;
// the dev proxy forwards /operator/* to the backend (no Access locally, so the
// token form is the dev path).
export function operatorServerBase(hostname: string, origin: string): string {
  if (hostname === "localhost" || hostname === "127.0.0.1") return origin;
  // Same-origin hosts (hosted-staging + the ops.plaintosee.cc console): the
  // FastAPI app serves the operator API same-origin (no Pages Function bridge),
  // so call the origin directly. The Google-login Access cookie is same-origin
  // and travels; auth falls back to the operator-token form if the JWT audience
  // doesn't match. Must stay in lockstep with resolveDefaultServer — both key
  // off isSameOriginHost. (On Pages, cloud.plaintosee.cc, we instead route
  // through the /operator/api/* bridge that forwards the JWT to the VPS.)
  if (isSameOriginHost(hostname)) return origin;
  return `${origin}/operator/api`;
}

function readServer(): string {
  if (typeof window === "undefined") return DEFAULT_SERVER;
  return operatorServerBase(window.location.hostname, window.location.origin);
}

async function probeJwtAuth(server: string): Promise<boolean> {
  // Single GET against the lightest read endpoint with no token
  // header. 200 means the server's hybrid gate accepted us via JWT;
  // any other status (403, network error) means we need the form.
  // The browser auto-attaches the CF_Authorization cookie when behind
  // CF Access, so we don't need to know its contents.
  try {
    const r = await fetch(`${server}/operator/applications`, {
      credentials: "include",
    });
    return r.status === 200;
  } catch {
    return false;
  }
}

export function OperatorShell() {
  const [server] = useState<string>(readServer);
  const [auth, setAuth] = useState<AuthState>(() => {
    const stored = readStoredToken();
    if (stored) return { mode: "ready", token: stored };
    return { mode: "probing" };
  });

  useEffect(() => {
    if (auth.mode !== "probing") return;
    let cancelled = false;
    probeJwtAuth(server).then((ok) => {
      if (cancelled) return;
      setAuth(ok ? { mode: "ready", token: "" } : { mode: "form" });
    });
    return () => {
      cancelled = true;
    };
  }, [auth.mode, server]);

  function handleAccept(t: string) {
    try {
      sessionStorage.setItem(OPERATOR_TOKEN_STORAGE_KEY, t);
    } catch {
      // ignore — token will live in-memory for this session
    }
    setAuth({ mode: "ready", token: t });
  }

  function handleClearToken() {
    try {
      sessionStorage.removeItem(OPERATOR_TOKEN_STORAGE_KEY);
    } catch {
      // ignore
    }
    setAuth({ mode: "form" });
  }

  if (auth.mode === "probing") {
    return <OperatorProbing />;
  }
  if (auth.mode === "form") {
    return <OperatorTokenEntry onAccept={handleAccept} />;
  }
  // JWT mode: token === "". Hide the sign-out button — CF Access
  // owns the session; signing out is "log out from the OTP edge",
  // which the dashboard's "Sign out" can't accomplish anyway.
  const showSignOut = auth.token !== "";
  return (
    <OperatorDashboard
      server={server}
      token={auth.token}
      onSignOut={showSignOut ? handleClearToken : undefined}
    />
  );
}


function OperatorProbing() {
  // Brief loading splash so we don't flash the token form on every
  // page load in JWT mode. Reuses the operator-entry visual register
  // so the transition into the dashboard or the form is visually
  // quiet.
  return (
    <div className="operator-entry">
      <header className="operator-entry-head">
        <h1>Aurora — operator</h1>
        <p className="muted">Checking authentication…</p>
      </header>
    </div>
  );
}


type EntryProps = { onAccept: (token: string) => void };

function OperatorTokenEntry({ onAccept }: EntryProps) {
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = input.trim();
    if (!trimmed) {
      setError("Enter the operator token.");
      return;
    }
    onAccept(trimmed);
  }

  return (
    <div className="operator-entry">
      <header className="operator-entry-head">
        <h1>Aurora — operator</h1>
        <p className="muted">
          Internal surface — Cloudflare Access OTP + operator token.
        </p>
      </header>
      <form onSubmit={handleSubmit} className="operator-entry-form">
        <label>
          <span>Operator token</span>
          <input
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="X-Operator-Token"
          />
        </label>
        {error && <div className="operator-entry-error">{error}</div>}
        <button type="submit" className="operator-entry-submit">
          Continue
        </button>
      </form>
    </div>
  );
}
